<template>
  <Grid
    class="ingredient-details-grid"
    :data-items="ingredientDetails"
    :columns="columns"
    :loader="loading"
    :resizable="true"
    :showOriginal="false"
    ref="detailIngredientsGridRef"
    data-cy="ingredient-details-grid"
  >
    <template v-slot:OnScheduleACell="{ props: { dataItem, field  } }">
      <td class="k-table-td">
        <span>{{ dataItem[field] ? "Yes" : "No" }}</span>
      </td>
    </template>
    <template v-slot:StatusCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <DropDownList
          v-if="canEditIarLineItemStatus(dataItem)"
          class="status-dropdown"
          :class="{ processed: dataItem.MissingOnSchedA !== 'Y' }"
          :dataItems="statusOptions.map((x) => x.fieldValue)"
          :value="dataItem[field]"
          :loading="dataItem.loading"
          @change="(e) => onStatusChange(e, dataItem)"
          data-cy="status-dropdown"
        />
        <span v-else>{{ dataItem[field] }}</span>
      </td>
    </template>
    <template v-slot:AttachmentsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <a
          v-if="dataItem.Attachments?.length > 0"
          class="is-download-link"
          @click="downloadAttachment(dataItem)"
          data-cy="download-attachment"
        >
          <font-awesome-icon icon="fa-solid fa-download" />
          {{
            dataItem.Attachments.length == 1
              ? dataItem.Attachments[0].Name
              : `Download ${dataItem.Attachments.length} Documents`
          }}
        </a>
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import { ref, watch, watchEffect } from "vue";
import { Grid } from "@progress/kendo-vue-grid";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type { IngredientSubmissionDetail } from "@/Domain/IngredientSubmissionDetail";
import {
  DropDownList,
  type DropDownListChangeEvent,
} from "@progress/kendo-vue-dropdowns";
import type { SubmissionStatus } from "@/Domain/SubmissionStatus";
import { api } from "@/api";
import type { UpdateIngredientSubmissionStatusRequest } from "@/Models/Requests/UpdateIngredientSubmissionStatusRequest";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useMainStore } from "@/stores/useMainStore";
import GridController from "@/shared/services/GridController";

interface IngredientSubmissionDetailLoading extends IngredientSubmissionDetail {
  loading: boolean;
}

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  ingredientDetails: IngredientSubmissionDetail[];
  loading: boolean;
  showOriginal: boolean;
  statusOptions: SubmissionStatus[];
  canEditIarLineItemStatus: (details: IngredientSubmissionDetail) => boolean;
}>();

const emit = defineEmits<{
  (e: "statusChanged"): void;
}>();

const getColumns = function () {
  const cols = [] as GridColumnPropsExtended[];

  cols.push(
    {
      title: "Line",
      field: "LineItem",
      minWidth: 50,
    },
    {
      title: "Ingredient",
      field: "Ingredient",
      minWidth: 80,
    }
  );

  if (props.showOriginal) {
    cols.push({
      title: "Orig Ingredient",
      field: "OriginalIngredient",
      minWidth: 80,
    });
  }

  cols.push({
    title: "Brand",
    field: "Brand",
    minWidth: 80,
  });

  if (props.showOriginal) {
    cols.push({
      title: "Orig Brand",
      field: "OriginalBrand",
      minWidth: 80,
    });
  }

  cols.push({
    title: "Source",
    field: "Source",
    minWidth: 80,
  });

  if (props.showOriginal) {
    cols.push({
      title: "Orig Source",
      field: "OriginalSource",
      minWidth: 80,
    });
  }

  cols.push(
    {
      title: "UKD Id",
      field: "UKDID",
      minWidth: 80,
    },
    {
      title: "Plant Name",
      field: "PlantName",
      minWidth: 100,
    },
    {
      title: "RMC",
      field: "rmc",
      minWidth: 70,
    },
    {
      title: "On Schedule A",
      field: "OnScheduleA",
      cell: "OnScheduleACell",
      minWidth: 100,
    },
    {
      title: "Status",
      field: "Status",
      cell: "StatusCell",
      minWidth: 300,
      width: "300px",
    },
    {
      title: "Attachments",
      field: "Attachments",
      cell: "AttachmentsCell",
      minWidth: 100,
    },
    {
      title: "Reviewed By",
      field: "ApprovedBy",
      minWidth: 80,
    },
    {
      title: "Reviewed Date",
      field: "ApprovalDate",
      type: "date",
      format: "{0:MM/dd/yyyy h:mm a}",
      minWidth: 100,
    }
  );

  return cols;
};

const columns = ref<GridColumnPropsExtended[]>(getColumns());
const controller = new GridController<IngredientSubmissionDetail>(
  "WorkflowDetailIngredientsGrid",
  columns.value
);
const detailIngredientsGridRef = ref<InstanceType<typeof Grid> | null>(null);

const onStatusChange = async function (
  e: DropDownListChangeEvent,
  details: IngredientSubmissionDetailLoading
) {
  if (!details) return;
  try {
    details.loading = true;

    const newStatus = props.statusOptions.find(
      (x) => x.fieldValue === e.value
    ) as SubmissionStatus;
    if (!newStatus || newStatus.status == null) return;

    const apiPromise = api.post("UpdateIngredientSubmissionStatus", {
      JobID: Number(details.JobId),
      LineItemNumber: details.LineItem,
      IngredientStatus: newStatus.fieldValue,
    } as UpdateIngredientSubmissionStatusRequest);

    await handleResponsePromiseAsync(
      apiPromise,
      () => {
        details.Status = newStatus.fieldValue;
        emit("statusChanged");
        notificationStore.notify({
          message: "Ingredient job status updated",
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      },
      "WorkflowDetailIngredientsGrid.vue",
      "onStatusChange",
      () => {
        notificationStore.notify({
          message: "Error updating status. Please try again",
          type: NotificationTypeEnum.error,
          duration: 5000,
        });
      }
    );
  } finally {
    details.loading = false;
  }
};

const downloadAttachment = function (submission: IngredientSubmissionDetail) {
  return api.getDownload("GetSubmissionDetailsAttachment", {
    jobId: submission.JobId,
    inCartId: submission.IngredientInCartID?.toString() ?? "",
    submissionType: "Ingredient",
  });
};

watch(
  () => props.showOriginal,
  () => {
    columns.value = getColumns();
    controller.columns.value = columns.value;
    controller.handleResize();
  }
);

watchEffect(() => {
  if (detailIngredientsGridRef.value && !controller.gridRef.value) {
    controller.setGridRef(detailIngredientsGridRef);
  }
});
</script>

<style scoped lang="scss">
.status-dropdown {
  &.processed {
    background-color: darkgrey;
  }
}

.ingredient-details-grid {
  min-height: 150px;
}

.k-grid {
  a.is-download-link {
    cursor: pointer;
    svg {
      height: 12px;
    }

    &:before {
      content: "";
    }
  }

  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
}
</style>
